import { ReactNode, useCallback, useContext, useEffect } from 'react'
import { useMatches, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import useSWRMutation from 'swr/mutation'
import {
  Container,
  FormDivider,
  PageSubtitle,
  PageTitle,
  TextHeaderBox,
  PageHeaderBox,
  ButtonHeaderBox,
  PageOptions,
  ContainerButtons,
  ContainerCard,
  DetailCard,
  IconDetailContainer,
  LabelCard,
  CardTitle,
  CardDescription,
  CardAction,
  StatsGroup,
  StatsGroupTitle,
  ContainerBox,
  ContentBox,
  BoxTitle,
  BoxSubtitle,
  ContractServiceButton,
  ImgBdCrmBonus,
  CalendarDashboardStyled,
} from './styles'

import { DashboardhSmallButton, Icon } from 'bd-web-components'

import { getDashColor } from '../../../styles'
import {
  PRIMARY_RED_01,
  SECONDARY_BLUE_02,
  SECONDARY_GREEN_07,
  SECONDARY_YELLOW_05,
  SECONDARY_ORANGE_01,
} from 'bd-web-components/src/utils/dash-colors'

import { useAbandonedCart } from '../../../hooks/abandoned-cart/legacy'
import BdCrmBonus from '../../../assets/bd-crmbonus.png'
import Loader from '../../../components/Loader'
import { useGuard } from '../../../hooks/auth'
import DateContext from '../../../hooks/date'
import { Breadcrumb } from '../../../components/Breadcrumb'
import { baixarCsv } from '../../OldAdvanceAnalytics/page-helper'
import { formatCurrencyBRL } from '../../../utils/format-currency-without-rs'

type OrderSessionsStats = {
  abandoned?: number
  completed?: number
  abandonedPercentage?: string
  uniqueClients?: number
  averageSessionsPerClient?: string
  totalSessions?: number
  averageTicket?: string
  ticketSum?: string
  exportableSessions?: number
  nonExportableSessions?: number
}

type StatCardProps = {
  isLoading: boolean
  iconName: string
  iconFill: string
  iconClass?: string
  label: string
  description: string
  value: string | number
  action?: ReactNode
}

function formatInteger(value?: number) {
  return (value ?? 0).toLocaleString('pt-BR')
}

function StatCard({
  isLoading,
  iconName,
  iconFill,
  iconClass,
  label,
  description,
  value,
  action,
}: StatCardProps) {
  return (
    <ContainerCard>
      <DetailCard>
        <IconDetailContainer className={iconClass}>
          <div>
            <Icon
              fill={iconFill}
              classes="question-circle-help-center"
              iconName={iconName}
              width={20}
              height={20}
            />
          </div>
        </IconDetailContainer>
        <LabelCard>
          {isLoading && <Loader small noMargin />}
          {!isLoading && (
            <>
              {label}
              <CardTitle>{value}</CardTitle>
              <CardDescription>{description}</CardDescription>
              {action}
            </>
          )}
        </LabelCard>
      </DetailCard>
    </ContainerCard>
  )
}

export function LegacyAbandonedCart() {
  const maxDate: Date = new Date()

  useGuard(['DP9'])

  const { id } = useParams<{ id: string }>()
  const { stateDate } = useContext(DateContext)

  let matches = useMatches()
  let crumbs = matches
    .filter((match: any) => Boolean(match.handle?.crumb))
    .map((match: any) => {
      return {
        name: match.handle.crumb(match.data),
        path: match.pathname,
      }
    })

  const { useAbandonedCartFetcher, useAbandonedCartStatsFetcher } =
    useAbandonedCart(id, stateDate.endDate, stateDate.startDate)

  const {
    trigger: abandonedCartStatsTrigger,
    data: abandonedCartStatsResult,
    isMutating: statsIsLoading,
  }: any = useSWRMutation('abandonedCartSWR', useAbandonedCartStatsFetcher, {
    revalidate: true,
  })

  const { trigger: abandonedCartTrigger, isMutating: exportIsLoading }: any =
    useSWRMutation('abandonedCartSWR', useAbandonedCartFetcher, {
      revalidate: false,
    })

  const handlerStats = useCallback(async () => {
    try {
      await abandonedCartStatsTrigger()
    } catch (error: any) {
      toast.error('Um erro aconteceu, tente novamente')
    }
  }, [abandonedCartStatsTrigger])

  const handlerExports = useCallback(async () => {
    try {
      const result = await abandonedCartTrigger()
      const { data } = result
      baixarCsv(
        data.orderSessions,
        `carrinho-abandonado-${stateDate.startDate}-${stateDate.endDate}.csv`
      )

      toast.success('Download concluído!')
    } catch (error: any) {
      toast.error('Um erro aconteceu, tente novamente')
    }
  }, [abandonedCartTrigger, stateDate.startDate, stateDate.endDate])

  useEffect(() => {
    console.log(stateDate)

    handlerStats()
  }, [stateDate, handlerStats])

  const orderSessions: OrderSessionsStats =
    abandonedCartStatsResult?.data?.orderSessions ?? {}
  const exportableSessions = orderSessions.exportableSessions ?? 0
  const startedCarts =
    (orderSessions.exportableSessions ?? 0) + (orderSessions.completed ?? 0)

  return (
    <>
      <Container>
        <Breadcrumb paths={[
          { name: 'Início', path: '/events' },
          { name: 'Evento', path: `/events/${id}` },
          ...crumbs,
        ]} />
        <PageHeaderBox>
          <TextHeaderBox>
            <PageTitle>Carrinho abandonado</PageTitle>
            <PageSubtitle>
              Confira os carrinhos abandonados, o valor das vendas não
              concluídas e os contatos para recuperação.
            </PageSubtitle>
          </TextHeaderBox>
        </PageHeaderBox>
        <FormDivider />
        <PageHeaderBox>
          <TextHeaderBox>
            <PageOptions>Relatório</PageOptions>
          </TextHeaderBox>
          <ButtonHeaderBox>
            <CalendarDashboardStyled maxDate={maxDate} />
          </ButtonHeaderBox>
        </PageHeaderBox>
        <StatsGroup>
          <StatsGroupTitle>Jornada de compra</StatsGroupTitle>
          <ContainerButtons>
            <StatCard
              isLoading={statsIsLoading}
              iconName="creditCard"
              iconFill={SECONDARY_BLUE_02}
              iconClass="graph"
              label="Carrinhos iniciados"
              description="Total de carrinhos que chegaram à etapa de checkout."
              value={formatInteger(startedCarts)}
            />
            <StatCard
              isLoading={statsIsLoading}
              iconName="shoppingCart"
              iconFill={SECONDARY_ORANGE_01}
              iconClass="warning"
              label="Carrinhos abandonados"
              description="Carrinhos que chegaram ao checkout, mas não tiveram a compra finalizada."
              value={formatInteger(orderSessions.exportableSessions)}
              action={
                exportableSessions > 0 ? (
                  <CardAction>
                    <DashboardhSmallButton
                      onClick={() => handlerExports()}
                      color="#fff"
                      background={getDashColor('SECONDARY_BLUE_01', 'GRAY_DARK')}
                      height="36px"
                      maxWidth="100%"
                      variant="contained"
                      width="100%"
                      disabled={exportIsLoading}
                      borderRadius="100px"
                    >
                      {!exportIsLoading && 'Exportar dados'}
                      {exportIsLoading && `Exportando...`}
                    </DashboardhSmallButton>
                  </CardAction>
                ) : undefined
              }
            />
            <StatCard
              isLoading={statsIsLoading}
              iconName="checkedCircle"
              iconFill={SECONDARY_GREEN_07}
              iconClass="success"
              label="Compras finalizadas"
              description="Carrinhos que tiveram a compra concluída no período selecionado."
              value={formatInteger(orderSessions.completed)}
            />
          </ContainerButtons>
        </StatsGroup>
        <StatsGroup>
          <StatsGroupTitle>Valor dos carrinhos abandonados</StatsGroupTitle>
          <ContainerButtons>
            <StatCard
              isLoading={statsIsLoading}
              iconName="dashboardMoney"
              iconFill={SECONDARY_BLUE_02}
              iconClass="graph"
              label="Valor total"
              description="Valor total dos carrinhos que não foram concluídos."
              value={formatCurrencyBRL(Number(orderSessions.ticketSum ?? 0))}
            />
            <StatCard
              isLoading={statsIsLoading}
              iconName="goldBar"
              iconFill={SECONDARY_YELLOW_05}
              iconClass="avg"
              label="Ticket médio"
              description="Valor médio de cada carrinho abandonado."
              value={formatCurrencyBRL(Number(orderSessions.averageTicket ?? 0))}
            />
          </ContainerButtons>
        </StatsGroup>
        <StatsGroup>
          <StatsGroupTitle>Acessos à página do evento</StatsGroupTitle>
          <ContainerButtons>
            <StatCard
              isLoading={statsIsLoading}
              iconName="userProfile"
              iconFill={PRIMARY_RED_01}
              label="Acessos à página do evento"
              description="Total de acessos à página do evento no período selecionado."
              value={formatInteger(orderSessions.abandoned)}
            />
            <StatCard
              isLoading={statsIsLoading}
              iconName="personLine"
              iconFill={SECONDARY_BLUE_02}
              iconClass="graph"
              label="Clientes únicos"
              description="Quantidade de clientes diferentes que acessaram a página do evento."
              value={formatInteger(orderSessions.uniqueClients)}
            />
          </ContainerButtons>
        </StatsGroup>
        {false && (
          <ContainerBox>
            <ContentBox>
              <BoxTitle>Recupere seu carrinho pelo WhatsApp!</BoxTitle>
              <BoxSubtitle>
                Finalize sua compra de forma rápida e fácil com
                <br />
                um clique no WhatsApp. 🚀
              </BoxSubtitle>

              <ContractServiceButton
                onClick={() => console.log(`exportar dados`)}
              >
                CONTRATAR ESSE SERVIÇO
              </ContractServiceButton>
            </ContentBox>
            <ImgBdCrmBonus src={BdCrmBonus} />
          </ContainerBox>
        )}
      </Container>
    </>
  )
}
